import { defaultLocale } from '@pkg/config/languages';
import { SINGLETON } from '@pkg/config/sanity/schemas';
import { SITE_IDS, WWW_AUDIENCE_FILTER } from '@pkg/config/sanity/audience';
import { defaultSeoFields } from '@pkg/sanity-utils/queries/defaultSeoFields';
import { seoConfigDocumentId } from '@pkg/sanity-utils/seoConfigDocumentId';
import type { DefaultSeo } from '@pkg/sanity-utils/types/seo';
import { AppStore } from '@pkg/sanity-utils/constants/appstore';
import { Social } from '@pkg/sanity-utils/constants/social';
import type { Reference } from '@sanity/types';
import type { IMAGE_FIT } from '@pkg/sanity-utils/constants/imageFit';
import { footerNavigationItems, mainNav } from './partials/siteNavigation';
import type { MainNav, FooterNavigationItemsQuery } from './partials/siteNavigation';

export interface FallbackImage {
  alt?: string;
  fit?: IMAGE_FIT,
  asset: Reference
}

export interface SiteManifestQuery {
  regionTheme: {
    mainNavigation: null | MainNav;
    mainFooter: null | FooterNavigationItemsQuery;
  };
  defaultSeo: null | DefaultSeo;
  appStores: {
    modalTitleAppStoreDownload: string | null;
    [AppStore.APPLE_APP_STORE]: string | null;
    [AppStore.GOOGLE_PLAY_STORE]: string | null;
    [AppStore.HUAWEI_APP_GALLERY]: string | null;
    [AppStore.APPLE_APP_STORE_BADGE]: string | null
    [AppStore.GOOGLE_PLAY_STORE_BADGE]: string | null
    [AppStore.HUAWEI_APP_GALLERY_BADGE]: string | null
  };
  social: {
    [Social.FACEBOOK]: string | null;
    [Social.FACEBOOK_API_KEY]: string | null;
    [Social.INSTAGRAM]: string | null;
    [Social.LINKEDIN]: string | null;
    [Social.TWITTER]: string | null;
    [Social.YOUTUBE]: string | null;
    [Social.TIKTOK]: string | null;
  };
  slugs: null | {
    [key: string]: string
  };
  fallbackImages: {
    press: FallbackImage,
    resource: FallbackImage,
  }
}

export const siteManifest = groq`{
  "regionTheme": *[_type == "${SINGLETON.THEME}" && language == $locale && ${WWW_AUDIENCE_FILTER}][0] {
    ...,
    activeAnnouncement->{...},
    "mainNavigation": ${mainNav},
    mainFooter->{${footerNavigationItems}},
  },
  "defaultSeo": *[_id == "${seoConfigDocumentId(SITE_IDS.WWW)}"][0] {
    ${defaultSeoFields}
  },
  "appStores": *[_type == "${SINGLETON.CONFIG_APPSTORES}"][0] {
    "modalTitleAppStoreDownload": coalesce(
      modalTitleAppStoreDownload[language == $locale][0].value,
      modalTitleAppStoreDownload[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.APPLE_APP_STORE}": coalesce(
      ${AppStore.APPLE_APP_STORE}[language == $locale][0].value,
      ${AppStore.APPLE_APP_STORE}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.GOOGLE_PLAY_STORE}": coalesce(
      ${AppStore.GOOGLE_PLAY_STORE}[language == $locale][0].value,
      ${AppStore.GOOGLE_PLAY_STORE}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.HUAWEI_APP_GALLERY}": coalesce(
      ${AppStore.HUAWEI_APP_GALLERY}[language == $locale][0].value,
      ${AppStore.HUAWEI_APP_GALLERY}[language == "${defaultLocale}"][0].value,
    ),
    "${AppStore.APPLE_APP_STORE_BADGE}": coalesce(
      ${AppStore.APPLE_APP_STORE_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.APPLE_APP_STORE_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
    "${AppStore.GOOGLE_PLAY_STORE_BADGE}": coalesce(
      ${AppStore.GOOGLE_PLAY_STORE_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.GOOGLE_PLAY_STORE_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
    "${AppStore.HUAWEI_APP_GALLERY_BADGE}": coalesce(
      ${AppStore.HUAWEI_APP_GALLERY_BADGE}[language == $locale][0].value.asset->url,
      ${AppStore.HUAWEI_APP_GALLERY_BADGE}[language == "${defaultLocale}"][0].value.asset->url,
    ),
  },
  "social": *[_type == "${SINGLETON.CONFIG_SOCIAL}"][0] {
    "${Social.FACEBOOK}": ${Social.FACEBOOK}[language == $locale][0].value,
    "${Social.FACEBOOK_API_KEY}": ${Social.FACEBOOK_API_KEY}[language == $locale][0].value,
    "${Social.INSTAGRAM}": ${Social.INSTAGRAM}[language == $locale][0].value,
    "${Social.LINKEDIN}": ${Social.LINKEDIN}[language == $locale][0].value,
    "${Social.TWITTER}": ${Social.TWITTER}[language == $locale][0].value,
    "${Social.YOUTUBE}": ${Social.YOUTUBE}[language == $locale][0].value,
    "${Social.TIKTOK}": ${Social.TIKTOK}[language == $locale][0].value,
  },
  "slugs": {
    "blog": *[_id == $blogSingletonDocId][0].slug.current,
    "careers": *[_id == $careersSingletonDocId][0].slug.current,
    "pressReleases": *[_id == $pressReleaseSingletonDocId][0].slug.current,
  },
  "fallbackImages": *[_type == "${SINGLETON.CONFIG_PLACEHOLDER_IMAGES}"][0] {
    "press": defaultImagePress {
      fit,
      alt,
      asset,
    },
    "resource": defaultImageResources {
      fit,
      alt,
      asset
    }
  }
}`;
